import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { AlertTriangle, CheckCircle } from 'lucide-react';

function Alerts() {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAlerts();
  }, []);

  const fetchAlerts = async () => {
    try {
      const res = await axios.get(`${API}/alerts`);
      setAlerts(res.data.alerts);
    } catch (error) {
      toast.error('Failed to load alerts');
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (alertId) => {
    try {
      await axios.put(`${API}/alerts/${alertId}/read`);
      toast.success('Alert marked as read');
      fetchAlerts();
    } catch (error) {
      toast.error('Failed to update alert');
    }
  };

  const getAlertStyle = (type) => {
    switch (type) {
      case 'stock_low':
        return 'border-warning bg-warning/5';
      case 'loss_high':
        return 'border-loss bg-loss/5';
      case 'cash_high':
        return 'border-accent bg-accent/5';
      default:
        return 'border-muted bg-muted/5';
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="alerts-page">
        <div className="flex justify-between items-center">
          <h2 className="text-2xl font-heading font-bold">Alerts & Notifications</h2>
          <div className="text-sm text-muted">
            {alerts.filter(a => !a.is_read).length} unread
          </div>
        </div>

        {loading ? (
          <div className="text-center py-8 text-muted">Loading...</div>
        ) : alerts.length === 0 ? (
          <Card className="p-8 text-center border border-border">
            <CheckCircle className="w-12 h-12 mx-auto mb-4 text-success" />
            <p className="text-muted">No alerts. Everything is running smoothly!</p>
          </Card>
        ) : (
          <div className="space-y-4">
            {alerts.map((alert) => (
              <Card 
                key={alert.id} 
                className={`p-6 border ${getAlertStyle(alert.alert_type)} ${alert.is_read ? 'opacity-60' : ''}`}
                data-testid="alert-card"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4 flex-1">
                    <AlertTriangle className="w-6 h-6 text-warning flex-shrink-0 mt-1" />
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-semibold capitalize">{alert.alert_type.replace('_', ' ')}</h3>
                        {!alert.is_read && (
                          <span className="px-2 py-0.5 bg-warning text-white text-xs rounded-full">
                            New
                          </span>
                        )}
                      </div>
                      <p className="text-sm mb-2">{alert.message}</p>
                      <p className="text-xs text-muted">
                        {new Date(alert.created_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                  {!alert.is_read && (
                    <Button
                      data-testid="mark-read-button"
                      variant="outline"
                      size="sm"
                      onClick={() => markAsRead(alert.id)}
                    >
                      Mark as Read
                    </Button>
                  )}
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

export default Alerts;